import React from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import {
  displayUserName,
  generateTempPassword,
  userInitialsSource,
} from '../../utils/userDisplay';
import UserManagementAvatar from './UserManagementAvatar';

export default function UserManagementModals({
  showAddUserModal,
  onCloseAddUserModal,
  newUserForm,
  onNewUserFormChange,
  onCreateUser,
  createdPassword,
  busy,

  showEditUserModal,
  editingUser,
  onCancelEditing,
  onEditInputChange,
  onSaveChanges,

  passwordTarget,
  onClosePasswordModal,
  onSendPasswordReset,

  deleteTarget,
  onCloseDeleteModal,
  onConfirmDelete,

  showUnitsModal,
  onCloseUnitsModal,
  units,
  newUnitName,
  newUnitDescription,
  onNewUnitNameChange,
  onNewUnitDescriptionChange,
  onAddUnit,
  onDeleteUnit,
}) {
  return (
    <>
      <Modal
        show={showAddUserModal}
        onHide={onCloseAddUserModal}
        size="lg"
        scrollable
        dialogClassName="user-management-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Dodaj uporabnika</Modal.Title>
        </Modal.Header>
        <Form onSubmit={onCreateUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Elektronski naslov *</Form.Label>
              <Form.Control
                type="email"
                required
                value={newUserForm.email}
                onChange={(event) => onNewUserFormChange({ email: event.target.value })}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Ime</Form.Label>
                  <Form.Control
                    value={newUserForm.firstName}
                    onChange={(event) => onNewUserFormChange({ firstName: event.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Priimek</Form.Label>
                  <Form.Control
                    value={newUserForm.lastName}
                    onChange={(event) => onNewUserFormChange({ lastName: event.target.value })}
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Telefon</Form.Label>
              <Form.Control
                value={newUserForm.phone}
                onChange={(event) => onNewUserFormChange({ phone: event.target.value })}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Delovno mesto *</Form.Label>
                  <Form.Control
                    required
                    value={newUserForm.jobPosition}
                    onChange={(event) => onNewUserFormChange({ jobPosition: event.target.value })}
                    placeholder="npr. Referent"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Oddelek / področje dela *</Form.Label>
                  <Form.Control
                    required
                    value={newUserForm.workArea}
                    onChange={(event) => onNewUserFormChange({ workArea: event.target.value })}
                    placeholder="npr. Kadrovska služba"
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Začasno geslo *</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  required
                  minLength={6}
                  value={newUserForm.password}
                  onChange={(event) => onNewUserFormChange({ password: event.target.value })}
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => onNewUserFormChange({ password: generateTempPassword() })}
                >
                  Novo
                </Button>
              </div>
              <Form.Text className="text-muted">
                Posredujte geslo uporabniku varno. Minimum 6 znakov.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Vloga</Form.Label>
              <Form.Select
                value={newUserForm.role}
                onChange={(event) => onNewUserFormChange({ role: event.target.value })}
              >
                <option value="user">Uporabnik</option>
                <option value="local-admin">Administrator enote</option>
                <option value="admin">Administrator</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Enota</Form.Label>
              <Form.Select
                value={newUserForm.unit}
                onChange={(event) => onNewUserFormChange({ unit: event.target.value })}
              >
                <option value="">Brez enote</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.unitName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <div className="user-management-modal__field-spacer" aria-hidden="true" />
            {createdPassword && (
              <Alert variant="info" className="mt-3 mb-0">
                Uporabnik je ustvarjen. Geslo: <strong>{createdPassword}</strong>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onCloseAddUserModal}>
              Zapri
            </Button>
            <Button variant="primary" type="submit" disabled={busy === 'create'}>
              {busy === 'create' ? 'Ustvarjanje...' : 'Ustvari uporabnika'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showEditUserModal}
        onHide={onCancelEditing}
        size="lg"
        scrollable
        dialogClassName="user-management-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Uredi uporabnika</Modal.Title>
        </Modal.Header>
        {editingUser && (
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              onSaveChanges();
            }}
          >
            <Modal.Body>
              <div className="user-management-edit-profile mb-3">
                <UserManagementAvatar
                  user={editingUser}
                  displayName={userInitialsSource(editingUser)}
                />
                <div className="user-management-edit-profile__body">
                  <div className="user-management-edit-profile__name">
                    {displayUserName(editingUser)}
                  </div>
                  <div className="user-management-edit-profile__email">{editingUser.email}</div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Ime</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={editingUser.firstName || ''}
                      onChange={onEditInputChange}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Priimek</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={editingUser.lastName || ''}
                      onChange={onEditInputChange}
                    />
                  </Form.Group>
                </div>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Prikazno ime</Form.Label>
                <Form.Control
                  type="text"
                  name="displayName"
                  value={editingUser.displayName || ''}
                  onChange={onEditInputChange}
                  placeholder="Kako se uporabnik prikaže v portalu"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Telefon</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={editingUser.phone || ''}
                  onChange={onEditInputChange}
                />
              </Form.Group>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Delovno mesto *</Form.Label>
                    <Form.Control
                      type="text"
                      name="jobPosition"
                      value={editingUser.jobPosition || ''}
                      onChange={onEditInputChange}
                      placeholder="npr. Referent"
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Oddelek / področje dela *</Form.Label>
                    <Form.Control
                      type="text"
                      name="workArea"
                      value={editingUser.workArea || ''}
                      onChange={onEditInputChange}
                      placeholder="npr. Kadrovska služba"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Vloga</Form.Label>
                <Form.Select
                  name="role"
                  value={editingUser.role || 'user'}
                  onChange={onEditInputChange}
                >
                  <option value="user">Uporabnik</option>
                  <option value="local-admin">Administrator enote</option>
                  <option value="admin">Administrator</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Enota</Form.Label>
                <Form.Select
                  name="unit"
                  value={editingUser.unit || ''}
                  onChange={onEditInputChange}
                >
                  <option value="">Brez enote</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.unitName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="user-management-modal__field-spacer" aria-hidden="true" />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onCancelEditing}>
                Prekliči
              </Button>
              <Button variant="primary" type="submit" disabled={busy === 'save'}>
                {busy === 'save' ? 'Shranjevanje...' : 'Shrani'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      <Modal show={Boolean(passwordTarget)} onHide={onClosePasswordModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Pošlji povezavo za geslo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {passwordTarget && (
            <>
              <div className="user-management-edit-profile mb-3">
                <UserManagementAvatar
                  user={passwordTarget}
                  displayName={userInitialsSource(passwordTarget)}
                />
                <div className="user-management-edit-profile__body">
                  <div className="user-management-edit-profile__name">
                    {displayUserName(passwordTarget)}
                  </div>
                  <div className="user-management-edit-profile__email">{passwordTarget.email}</div>
                </div>
              </div>
              <p className="text-muted mb-0">
                Na ta email bomo poslali povezavo za nastavitev novega gesla.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClosePasswordModal}>
            Prekliči
          </Button>
          <Button
            variant="primary"
            onClick={onSendPasswordReset}
            disabled={busy === 'password'}
          >
            {busy === 'password' ? 'Pošiljam...' : 'Pošlji povezavo'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(deleteTarget)} onHide={onCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Izbriši uporabnika</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && (
            <>
              <p>
                Ali ste prepričani, da želite trajno izbrisati uporabnika{' '}
                <strong>{deleteTarget.email}</strong>?
              </p>
              <p className="text-muted small mb-0">
                Izbrisani bodo račun, profil in članstvo. Objavljena vsebina bo anonimizirana.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCloseDeleteModal}>
            Prekliči
          </Button>
          <Button variant="danger" onClick={onConfirmDelete} disabled={busy === 'delete'}>
            {busy === 'delete' ? 'Brisanje...' : 'Izbriši uporabnika'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showUnitsModal} onHide={onCloseUnitsModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Upravljanje enot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>Dodaj novo enoto</h5>
          <Form.Group controlId="formNewUnitName">
            <Form.Label>Ime enote</Form.Label>
            <Form.Control
              type="text"
              value={newUnitName}
              onChange={(event) => onNewUnitNameChange(event.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="formNewUnitDescription" className="mt-3">
            <Form.Label>Opis enote</Form.Label>
            <Form.Control
              type="text"
              value={newUnitDescription}
              onChange={(event) => onNewUnitDescriptionChange(event.target.value)}
            />
          </Form.Group>
          <Button variant="primary" className="mt-3" onClick={onAddUnit}>
            Dodaj enoto
          </Button>
          <hr />
          <h5>Seznam enot</h5>
          <Table striped bordered hover className="mt-3">
            <thead>
              <tr>
                <th>Ime enote</th>
                <th>Opis</th>
                <th>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {units.map((unit) => (
                <tr key={unit.id}>
                  <td>{unit.unitName}</td>
                  <td>{unit.description}</td>
                  <td>
                    <Button variant="danger" size="sm" onClick={() => onDeleteUnit(unit.id)}>
                      Izbriši
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCloseUnitsModal}>
            Zapri
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
